/* Nexus CRM — Import feature for People & Companies
   Self-contained: drag/drop CSV+XLSX → field mapping → validation →
   duplicate detection (merge / skip / new) → progress → summary → history.
   Uses existing store.create / store.update for actual writes. */

const { useState: useStateI, useRef: useRefI, useEffect: useEffectI, useMemo: useMemoI, useCallback: useCallbackI } = React;

/* ---------- Field schemas ---------- */
const IMPORT_SCHEMA = {
  people: {
    label: 'People',
    icon: 'User',
    fields: [
      { key: 'salutation',      label: 'Salutation',       required: false, aliases: ['salutation','title','prefix','honorific'] },
      { key: 'name',            label: 'Full Name',        required: true,  aliases: ['name','full name','contact','contact name','person'] },
      { key: 'email',           label: 'Email',            required: true,  aliases: ['email','e-mail','email address','mail'], validate: (v) => /^\S+@\S+\.\S+$/.test(String(v)) || 'Invalid email' },
      { key: 'phone',           label: 'Phone',            required: false, aliases: ['phone','mobile','contact number','phone number','telephone'] },
      { key: 'company',         label: 'Company',          required: true,  requiresCompany: true, aliases: ['company','organisation','organization','employer','firm','account'] },
      { key: 'owner',           label: 'Owner',            required: false, aliases: ['owner','rm','rm owner','relationship manager','assigned to','account owner'] },
      { key: 'tags',            label: 'Tags',             required: false, aliases: ['tags','labels','tag','segments'], multi: true },
      { key: 'loanType',        label: 'Loan Type',        required: false, aliases: ['loan type','loan','loan product'], group: 'BFSI' },
      { key: 'creditScore',     label: 'Credit Score',     required: false, aliases: ['credit score','cibil','bureau score','score'], type: 'number', group: 'BFSI', validate: (v) => { const n = Number(v); return (!isNaN(n) && n >= 300 && n <= 900) || 'Score must be 300–900'; } },
      { key: 'employmentType',  label: 'Employment Type',  required: false, aliases: ['employment','employment type','occupation type'], group: 'BFSI' },
      { key: 'income',          label: 'Income',           required: false, aliases: ['income','annual income','salary','ctc'], group: 'BFSI' },
      { key: 'repaymentStatus', label: 'Repayment Status', required: false, aliases: ['repayment','repayment status','emi status'], group: 'BFSI' },
      { key: 'riskLevel',       label: 'Risk Level',       required: false, aliases: ['risk','risk level','risk category','risk rating'], group: 'BFSI' },
    ],
    dedupe: (row, existing) => {
      const norm = (s) => String(s || '').trim().toLowerCase();
      if (row.email) return existing.find(p => norm(p.email) === norm(row.email));
      if (row.name) return existing.find(p => norm(p.name) === norm(row.name) && norm(p.company) === norm(row.company));
      return null;
    },
  },
  companies: {
    label: 'Companies',
    icon: 'Building',
    fields: [
      { key: 'name',         label: 'Company Name',  required: true,  aliases: ['name','company','company name','organization','firm','account name'] },
      { key: 'branch',       label: 'Branch',        required: false, aliases: ['branch','branch name','branch code','office','location'] },
      { key: 'owner',        label: 'RM Owner',      required: false, aliases: ['owner','rm','rm owner','relationship manager','assigned to'] },
      { key: 'productType',  label: 'Product Type',  required: false, aliases: ['product','product type','loan product','offering','line of business'] },
      { key: 'riskCategory', label: 'Risk Category', required: false, aliases: ['risk','risk category','risk level','rating'] },
      { key: 'domain',       label: 'Domain',        required: false, aliases: ['domain','website','url'] },
      { key: 'industry',     label: 'Industry',      required: false, aliases: ['industry','sector','vertical'] },
    ],
    dedupe: (row, existing) => {
      const norm = (s) => String(s || '').trim().toLowerCase();
      if (!row.name) return null;
      return existing.find(c => norm(c.name) === norm(row.name) || (row.domain && norm(c.domain) === norm(row.domain)));
    },
  },
  deals: {
    label: 'Deals',
    icon: 'Target',
    fields: [
      { key: 'title',     label: 'Deal Title',   required: true,  aliases: ['title','deal','deal name','opportunity','name'] },
      { key: 'value',     label: 'Value ($)',     required: true,  aliases: ['value','amount','deal value','revenue','deal amount'], type: 'number', validate: (v) => (!isNaN(Number(v)) && Number(v) >= 0) || 'Must be a number' },
      { key: 'company',   label: 'Company',       required: false, aliases: ['company','account','company name','organization','firm'] },
      { key: 'stage',     label: 'Stage',         required: false, aliases: ['stage','deal stage','pipeline stage','status'] },
      { key: 'priority',  label: 'Priority',      required: false, aliases: ['priority','deal priority','urgency'] },
      { key: 'owner',     label: 'Owner',         required: false, aliases: ['owner','assigned to','rm','rep','account executive'] },
      { key: 'closeDate', label: 'Close Date',    required: false, aliases: ['close date','expected close','close','closing date','target date'] },
    ],
    dedupe: (row, existing) => {
      const norm = (s) => String(s || '').trim().toLowerCase();
      if (!row.title) return null;
      return existing.find(d => norm(d.title) === norm(row.title) && norm(d.company || '') === norm(row.company || ''));
    },
  },
};
window.IMPORT_SCHEMA = IMPORT_SCHEMA;

/* ---------- CSV parser (RFC 4180-ish) ---------- */
const parseCSV = (text) => {
  const rows = [];
  let row = [], field = '', inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i], nxt = text[i + 1];
    if (inQuotes) {
      if (ch === '"' && nxt === '"') { field += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { field += ch; }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === ',') { row.push(field); field = ''; }
      else if (ch === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
      else if (ch === '\r') { /* skip */ }
      else { field += ch; }
    }
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows.filter(r => r.some(c => String(c).trim() !== ''));
};

/* ---------- XLSX via SheetJS (loaded by HTML) ---------- */
const parseXLSX = async (file) => {
  if (!window.XLSX) throw new Error('XLSX library not loaded');
  const buf = await file.arrayBuffer();
  const wb = window.XLSX.read(buf, { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const rows = window.XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' });
  return rows.filter(r => r.some(c => String(c).trim() !== ''));
};

/* ---------- File → 2D matrix ---------- */
const parseFile = async (file) => {
  const name = file.name.toLowerCase();
  if (name.endsWith('.csv')) {
    const text = await file.text();
    return parseCSV(text);
  }
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
    return await parseXLSX(file);
  }
  throw new Error('Unsupported file type. Use CSV or XLSX.');
};

/* ---------- Auto-detect mapping ---------- */
const autoMap = (headers, fields) => {
  const map = {};
  headers.forEach((h, i) => {
    const norm = String(h || '').trim().toLowerCase();
    if (!norm) return;
    const match = fields.find(f => f.aliases.some(a => a.toLowerCase() === norm) || f.key.toLowerCase() === norm || f.label.toLowerCase() === norm);
    if (match && !Object.values(map).includes(match.key)) map[i] = match.key;
  });
  return map;
};

/* ---------- Import history (localStorage) ---------- */
const HISTORY_KEY = 'nx-import-history-v1';
const loadHistory = () => { try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); } catch (_) { return []; } };
const saveHistoryEntry = (entry) => {
  const list = loadHistory();
  list.unshift(entry);
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(list.slice(0, 30))); } catch (_) {}
};

/* =========================================================
   ImportButton — entry point
   ========================================================= */
const ImportButton = ({ type }) => {
  const [open, setOpen] = useStateI(false);
  return (
    <>
      <Btn variant="ghost" icon="UploadCloud" onClick={() => setOpen(true)}>Import</Btn>
      {open && <ImportWizard type={type} onClose={() => setOpen(false)} />}
    </>
  );
};
window.ImportButton = ImportButton;

/* =========================================================
   ImportWizard — multi-step modal
   ========================================================= */
const ImportWizard = ({ type, onClose }) => {
  const store = useStore();
  const toast = useToast();
  const schema = IMPORT_SCHEMA[type];
  const fileInputRef = useRefI(null);

  const [step, setStep] = useStateI('upload'); // upload | map | review | progress | summary | history
  const [fileName, setFileName] = useStateI('');
  const [rawRows, setRawRows] = useStateI([]); // matrix incl header
  const [mapping, setMapping] = useStateI({}); // colIdx -> field.key
  const [headerRow, setHeaderRow] = useStateI(true);
  const [decisions, setDecisions] = useStateI({}); // rowIdx -> 'skip'|'new'|'merge'
  const [companyFix, setCompanyFix] = useStateI({}); // rowIdx -> companyName
  const [defaultCompany, setDefaultCompany] = useStateI('');
  const [progress, setProgress] = useStateI({ done: 0, total: 0 });
  const [summary, setSummary] = useStateI(null);
  const [dragOver, setDragOver] = useStateI(false);
  const [parseErr, setParseErr] = useStateI('');

  /* close on ESC */
  useEffectI(() => {
    const onKey = (e) => { if (e.key === 'Escape' && step !== 'progress') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, onClose]);

  /* ---------- Step: upload ---------- */
  const handleFile = useCallbackI(async (file) => {
    setParseErr('');
    if (!file) return;
    try {
      const matrix = await parseFile(file);
      if (!matrix.length) { setParseErr('File appears to be empty.'); return; }
      setFileName(file.name);
      setRawRows(matrix);
      const initialMap = autoMap(matrix[0] || [], schema.fields);
      setMapping(initialMap);
      setStep('map');
    } catch (e) {
      setParseErr(e.message || 'Failed to parse file.');
    }
  }, [schema]);

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  /* ---------- Build parsed rows from mapping ---------- */
  const parsedRows = useMemoI(() => {
    if (!rawRows.length) return [];
    const dataRows = headerRow ? rawRows.slice(1) : rawRows;
    return dataRows.map(r => {
      const obj = {};
      Object.entries(mapping).forEach(([colIdx, fieldKey]) => {
        const v = r[colIdx];
        if (v == null || v === '') return;
        const field = schema.fields.find(f => f.key === fieldKey);
        if (!field) return;
        if (field.multi) obj[fieldKey] = String(v).split(/[,;|]/).map(s => s.trim()).filter(Boolean);
        else if (field.type === 'number') obj[fieldKey] = Number(v);
        else obj[fieldKey] = String(v).trim();
      });
      return obj;
    });
  }, [rawRows, mapping, headerRow, schema]);

  /* ---------- Validation + dedupe analysis ---------- */
  const analysis = useMemoI(() => {
    const existing = store[type];
    const out = parsedRows.map((row, i) => {
      const errors = [];
      schema.fields.forEach(f => {
        if (f.required && (row[f.key] == null || row[f.key] === '' || (Array.isArray(row[f.key]) && !row[f.key].length))) {
          errors.push(f.label + ' is required');
        } else if (f.validate && row[f.key] != null && row[f.key] !== '') {
          const v = f.validate(row[f.key]);
          if (v !== true) errors.push(v);
        }
      });
      const duplicate = schema.dedupe(row, existing);
      const needsCompany = type === 'people' && (!row.company || String(row.company).trim() === '');
      return { idx: i, row, errors, duplicate, needsCompany };
    });
    return out;
  }, [parsedRows, store, schema, type]);

  const stats = useMemoI(() => {
    const total = analysis.length;
    const withErrors = analysis.filter(a => a.errors.length).length;
    const dupes = analysis.filter(a => a.duplicate).length;
    const missingCo = analysis.filter(a => a.needsCompany).length;
    return { total, withErrors, dupes, missingCo, ok: total - withErrors };
  }, [analysis]);

  /* default decisions: dupes -> 'merge', errors -> 'skip', otherwise 'new' */
  useEffectI(() => {
    if (step !== 'review') return;
    const d = {};
    analysis.forEach(a => {
      if (a.errors.length) d[a.idx] = 'skip';
      else if (a.duplicate) d[a.idx] = 'merge';
      else d[a.idx] = 'new';
    });
    setDecisions(d);
    setCompanyFix({});
  }, [step, analysis]);

  /* ---------- Run import ---------- */
  const runImport = async () => {
    setStep('progress');
    setProgress({ done: 0, total: analysis.length });
    let created = 0, merged = 0, skipped = 0, failed = 0;
    const errors = [];
    for (let i = 0; i < analysis.length; i++) {
      const a = analysis[i];
      const decision = decisions[a.idx] || 'skip';
      try {
        if (decision === 'skip') skipped++;
        else {
          let row = { ...a.row };
          if (type === 'people' && a.needsCompany) {
            row.company = companyFix[a.idx] || defaultCompany;
            if (!row.company) { failed++; errors.push('Row ' + (a.idx + 2) + ': missing company'); }
          }
          if (decision === 'merge' && a.duplicate) {
            const patch = {};
            Object.keys(row).forEach(k => { if (row[k] != null && row[k] !== '') patch[k] = row[k]; });
            store.update(type, a.duplicate.id, patch);
            merged++;
          } else if (decision === 'new' && !errors.length) {
            // attach companyId for people if a matching company exists
            const payload = { ...row };
            if (type === 'people') {
              const co = store.companies.find(c => String(c.name).toLowerCase() === String(payload.company || '').toLowerCase());
              if (co) payload.companyId = co.id;
              payload.status = payload.status || 'New';
              payload.owner = payload.owner || '';
              payload.score = payload.score || 50;
              payload.avatar = (payload.name || '').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase();
              payload.tone = '#125AB0';
              payload.lastActivity = 'just now';
              payload.tags = payload.tags || [];
            } else if (type === 'deals') {
              // Resolve company name → companyId
              if (payload.company) {
                const co = store.companies.find(c => String(c.name).toLowerCase() === String(payload.company).toLowerCase());
                if (co) payload.companyId = co.id;
              }
              // Resolve stage name → stageId using live pipeline (no hardcoded IDs)
              const pipeline = window.__nx_store?.pipeline || [];
              const firstStageId = pipeline[0]?.id || null;
              if (payload.stage) {
                const stageNorm = String(payload.stage).trim().toLowerCase();
                const matchedStage = pipeline.find(s => s.name.toLowerCase() === stageNorm);
                payload.stageId = matchedStage?.id || firstStageId;
                delete payload.stage;
              } else {
                payload.stageId = payload.stageId || firstStageId;
              }
              payload.value     = Number(payload.value) || 0;
              payload.priority  = payload.priority  || 'Medium';
              payload.owner     = payload.owner     || window.__nx_auth?.user?.name || '';
              payload.age       = 0;
              payload.lastTouch = 'just now';
              payload.alerts    = [];
            } else {
              payload.lifecycle = payload.lifecycle || 'Lead';
              payload.score = payload.score || 60;
              payload.employees = payload.employees || 0;
              payload.deals = 0;
              payload.lastActivity = 'just now';
            }
            store.create(type, payload, { skipDedupe: true });
            created++;
          }
        }
      } catch (e) {
        failed++;
        errors.push('Row ' + (a.idx + 2) + ': ' + (e.message || 'unknown error'));
      }
      setProgress({ done: i + 1, total: analysis.length });
      // throttle for visible progress
      if (i % 3 === 0) await new Promise(r => setTimeout(r, 30));
    }
    const result = {
      id: 'imp_' + Date.now(),
      type, fileName,
      at: new Date().toISOString(),
      created, merged, skipped, failed,
      total: analysis.length,
      errors: errors.slice(0, 20),
    };
    saveHistoryEntry(result);
    setSummary(result);
    setStep('summary');
    toast({ tone: 'success', icon: 'UploadCloud', title: 'Import Complete', desc: created + ' created · ' + merged + ' merged' });
  };

  /* ---------- Render ---------- */
  return (
    <div className="nx-cmd-veil" style={{ alignItems: 'center', zIndex: 90 }} onClick={() => { if (step !== 'progress') onClose(); }}>
      <div className="nx-import-modal" onClick={e => e.stopPropagation()}>
        <ImportHead schema={schema} step={step} onClose={onClose} onHistory={() => setStep('history')} />
        <div className="nx-import-body">
          {step === 'upload' && (
            <UploadStep
              fileInputRef={fileInputRef}
              dragOver={dragOver}
              setDragOver={setDragOver}
              onFile={handleFile}
              onDrop={onDrop}
              schema={schema}
              type={type}
              parseErr={parseErr}
            />
          )}
          {step === 'map' && (
            <MapStep
              schema={schema}
              rawRows={rawRows}
              mapping={mapping}
              setMapping={setMapping}
              headerRow={headerRow}
              setHeaderRow={setHeaderRow}
              fileName={fileName}
            />
          )}
          {step === 'review' && (
            <ReviewStep
              schema={schema}
              type={type}
              analysis={analysis}
              stats={stats}
              decisions={decisions}
              setDecisions={setDecisions}
              companyFix={companyFix}
              setCompanyFix={setCompanyFix}
              defaultCompany={defaultCompany}
              setDefaultCompany={setDefaultCompany}
              companies={store.companies}
            />
          )}
          {step === 'progress' && (
            <ProgressStep progress={progress} />
          )}
          {step === 'summary' && summary && (
            <SummaryStep summary={summary} schema={schema} />
          )}
          {step === 'history' && (
            <HistoryStep />
          )}
        </div>
        <ImportFoot
          step={step}
          stats={stats}
          decisions={decisions}
          analysis={analysis}
          companyFix={companyFix}
          defaultCompany={defaultCompany}
          mapping={mapping}
          schema={schema}
          onBack={() => {
            if (step === 'map') setStep('upload');
            else if (step === 'review') setStep('map');
            else if (step === 'summary' || step === 'history') onClose();
          }}
          onNext={() => {
            if (step === 'upload') fileInputRef.current?.click();
            else if (step === 'map') setStep('review');
            else if (step === 'review') runImport();
          }}
          onClose={onClose}
        />
      </div>
    </div>
  );
};

/* =========================================================
   Step components
   ========================================================= */

const ImportHead = ({ schema, step, onClose, onHistory }) => {
  const stepTitles = {
    upload:   'Upload File',
    map:      'Map Columns',
    review:   'Review & Resolve',
    progress: 'Importing…',
    summary:  'Import Complete',
    history:  'Import History',
  };
  const steps = ['upload', 'map', 'review', 'progress', 'summary'];
  const idx = steps.indexOf(step);
  return (
    <div className="nx-import-head">
      <div className="nx-import-head-top">
        <span className="nx-import-head-icon"><NexusIcon name={schema.icon} size={14} /></span>
        <div>
          <h3 className="nx-import-title">Import {schema.label}</h3>
          <span className="nx-import-sub">{stepTitles[step]}</span>
        </div>
        <span style={{ flex: 1 }} />
        {step !== 'progress' && step !== 'history' && step !== 'summary' && (
          <Btn variant="ghost" size="sm" icon="Clock" onClick={onHistory}>History</Btn>
        )}
        {step !== 'progress' && <IconBtn icon="X" onClick={onClose} />}
      </div>
      {idx >= 0 && step !== 'history' && (
        <div className="nx-import-steps">
          {['Upload','Map','Review','Import','Done'].map((s, i) => (
            <span key={s} className={'nx-import-step' + (i <= idx ? ' is-done' : '') + (i === idx ? ' is-active' : '')}>
              <span className="nx-import-step-dot">{i < idx ? <NexusIcon name="Check" size={10} /> : (i + 1)}</span>
              <span>{s}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const UploadStep = ({ fileInputRef, dragOver, setDragOver, onFile, onDrop, schema, type, parseErr }) => (
  <div className="nx-import-pad">
    <div
      className={'nx-import-drop' + (dragOver ? ' is-over' : '')}
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onClick={() => fileInputRef.current?.click()}
    >
      <span className="nx-import-drop-icon"><NexusIcon name="UploadCloud" size={28} /></span>
      <h4>Drag & drop your file here</h4>
      <p className="nx-muted">or click to browse · CSV or XLSX · up to 10,000 rows</p>
      <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={e => onFile(e.target.files?.[0])} />
      <Btn variant="primary" icon="UploadCloud" size="sm">Choose File</Btn>
    </div>
    {parseErr && <div className="nx-import-error"><NexusIcon name="AlertCircle" size={12} /> {parseErr}</div>}
    <div className="nx-import-template">
      <div>
        <span className="nx-prop-label">Expected columns</span>
        <div className="nx-import-cols">
          {schema.fields.map(f => (
            <span key={f.key} className={'nx-tag' + (f.required ? ' is-required' : '')} title={f.aliases.join(', ')}>
              {f.label}{f.required && <span style={{ color: 'var(--nx-danger)' }}> *</span>}
            </span>
          ))}
        </div>
      </div>
      <Btn variant="ghost" size="sm" icon="File" onClick={() => downloadTemplate(type, schema)}>Download CSV template</Btn>
    </div>
  </div>
);

const downloadTemplate = (type, schema) => {
  const header = schema.fields.map(f => f.label).join(',');
  const example = schema.fields.map(f => {
    if (f.key === 'email') return 'jane@example.com';
    if (f.key === 'phone') return '+91 98765 43210';
    if (f.key === 'name') return type === 'people' ? 'Jane Doe' : 'Acme Corp';
    if (f.key === 'company') return 'Acme Corp';
    if (f.key === 'owner') return window.__nx_auth?.user?.name || '';
    if (f.key === 'tags') return 'BFSI;VIP';
    if (f.key === 'loanType') return 'Personal Loan';
    if (f.key === 'creditScore') return '750';
    if (f.key === 'employmentType') return 'Salaried';
    if (f.key === 'income') return '₹20L';
    if (f.key === 'repaymentStatus') return 'On Time';
    if (f.key === 'riskLevel' || f.key === 'riskCategory') return 'Low';
    if (f.key === 'branch') return 'Mumbai-Central';
    if (f.key === 'productType') return 'Home Loan';
    if (f.key === 'domain') return 'acme.com';
    if (f.key === 'industry') return 'BFSI';
    return '';
  }).join(',');
  const blob = new Blob([header + '\n' + example], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = type + '-import-template.csv'; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const MapStep = ({ schema, rawRows, mapping, setMapping, headerRow, setHeaderRow, fileName }) => {
  const headers = headerRow ? (rawRows[0] || []) : (rawRows[0] || []).map((_, i) => 'Column ' + (i + 1));
  const sample = (headerRow ? rawRows.slice(1, 4) : rawRows.slice(0, 4));
  const required = schema.fields.filter(f => f.required);
  const mappedKeys = new Set(Object.values(mapping));
  const missingRequired = required.filter(f => !mappedKeys.has(f.key));
  const setCol = (i, key) => {
    setMapping(m => {
      const next = { ...m };
      // remove any prior column mapped to same key
      Object.keys(next).forEach(k => { if (next[k] === key) delete next[k]; });
      if (key) next[i] = key; else delete next[i];
      return next;
    });
  };
  return (
    <div className="nx-import-pad">
      <div className="nx-import-mapbar">
        <span className="nx-muted nx-mini"><NexusIcon name="File" size={11} /> {fileName} · {rawRows.length - (headerRow ? 1 : 0)} rows</span>
        <span style={{ flex: 1 }} />
        <label className="nx-mini" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={headerRow} onChange={e => setHeaderRow(e.target.checked)} />
          First row is header
        </label>
      </div>
      {missingRequired.length > 0 && (
        <div className="nx-import-warn"><NexusIcon name="AlertCircle" size={12} /> Map required fields: <strong>{missingRequired.map(f => f.label).join(', ')}</strong></div>
      )}
      <div className="nx-import-maptable">
        <div className="nx-import-maprow nx-import-maphead">
          <span>Source column</span>
          <span>Sample</span>
          <span>→</span>
          <span>URSA field</span>
        </div>
        {headers.map((h, i) => (
          <div key={i} className="nx-import-maprow">
            <span className="nx-import-mapname"><strong>{h || '(blank)'}</strong></span>
            <span className="nx-import-mapsample">
              {sample.map((r, j) => <span key={j}>{String(r[i] || '').slice(0, 30) || <em className="nx-muted">empty</em>}</span>)}
            </span>
            <span className="nx-muted">→</span>
            <select className="nx-input nx-import-mapsel" value={mapping[i] || ''} onChange={e => setCol(i, e.target.value)}>
              <option value="">— Skip column —</option>
              {schema.fields.map(f => (
                <option key={f.key} value={f.key} disabled={mappedKeys.has(f.key) && mapping[i] !== f.key}>
                  {f.label}{f.required ? ' *' : ''}{f.group ? ' (' + f.group + ')' : ''}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
};

const ReviewStep = ({ schema, type, analysis, stats, decisions, setDecisions, companyFix, setCompanyFix, defaultCompany, setDefaultCompany, companies }) => {
  const [filter, setFilter] = useStateI('all');
  const filtered = analysis.filter(a => {
    if (filter === 'errors') return a.errors.length;
    if (filter === 'dupes') return a.duplicate;
    if (filter === 'missingco') return a.needsCompany;
    return true;
  });
  const setDecision = (idx, val) => setDecisions(d => ({ ...d, [idx]: val }));
  const bulkAssignCo = (name) => {
    setDefaultCompany(name);
    setCompanyFix(m => {
      const next = { ...m };
      analysis.forEach(a => { if (a.needsCompany && !next[a.idx]) next[a.idx] = name; });
      return next;
    });
  };
  return (
    <div className="nx-import-pad">
      <div className="nx-import-stats">
        <StatChip label="Total" value={stats.total} icon="List" onClick={() => setFilter('all')} active={filter === 'all'} />
        <StatChip label="Ready" value={stats.total - stats.withErrors} icon="Check" tone="green" />
        <StatChip label="Errors" value={stats.withErrors} icon="AlertCircle" tone="red" onClick={() => setFilter('errors')} active={filter === 'errors'} />
        <StatChip label="Duplicates" value={stats.dupes} icon="Copy" tone="amber" onClick={() => setFilter('dupes')} active={filter === 'dupes'} />
        {type === 'people' && <StatChip label="Missing Company" value={stats.missingCo} icon="Building" tone="red" onClick={() => setFilter('missingco')} active={filter === 'missingco'} />}
      </div>

      {type === 'people' && stats.missingCo > 0 && (
        <div className="nx-import-bulkco">
          <NexusIcon name="Building" size={12} />
          <span><strong>{stats.missingCo}</strong> rows have no company — assign one before importing</span>
          <select className="nx-input" value={defaultCompany} onChange={e => bulkAssignCo(e.target.value)} style={{ marginLeft: 'auto', maxWidth: 220 }}>
            <option value="">Bulk-assign company…</option>
            {companies.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        </div>
      )}

      <div className="nx-import-rows">
        {filtered.length === 0 && <div className="nx-import-empty">No rows match this filter.</div>}
        {filtered.map(a => (
          <ReviewRow
            key={a.idx}
            entry={a}
            schema={schema}
            type={type}
            decision={decisions[a.idx]}
            onDecision={(v) => setDecision(a.idx, v)}
            companyFix={companyFix[a.idx]}
            onCompanyFix={(v) => setCompanyFix(m => ({ ...m, [a.idx]: v }))}
            companies={companies}
          />
        ))}
      </div>
    </div>
  );
};

const ReviewRow = ({ entry, schema, type, decision, onDecision, companyFix, onCompanyFix, companies }) => {
  const { row, errors, duplicate, needsCompany, idx } = entry;
  const title = row.name || row.email || '(row ' + (idx + 2) + ')';
  const subtitle = type === 'people'
    ? [row.email, row.company || (needsCompany ? <em key="m" style={{ color: 'var(--nx-danger)' }}>no company</em> : ''), row.phone].filter(Boolean).join(' · ')
    : [row.domain, row.branch, row.productType].filter(Boolean).join(' · ');
  const tone = errors.length ? 'red' : duplicate ? 'amber' : 'green';
  return (
    <div className={'nx-import-row tone-' + tone}>
      <div className="nx-import-row-main">
        <div className="nx-import-row-meta">
          <span className="nx-import-row-title">{title}</span>
          <span className="nx-import-row-sub">{subtitle}</span>
          {errors.length > 0 && (
            <span className="nx-import-row-err">
              {errors.map((e, i) => <span key={i}><NexusIcon name="AlertCircle" size={10} /> {e}</span>)}
            </span>
          )}
          {duplicate && (
            <span className="nx-import-row-dup">
              <NexusIcon name="Copy" size={10} />
              Matches existing: <strong>{duplicate.name}</strong>
              {duplicate.email && <span className="nx-muted"> · {duplicate.email}</span>}
            </span>
          )}
        </div>
        <div className="nx-import-row-actions">
          {needsCompany && (
            <select className="nx-input nx-input-sm" value={companyFix || ''} onChange={e => onCompanyFix(e.target.value)}>
              <option value="">Select company…</option>
              {companies.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          )}
          <div className="nx-segment">
            <button className={'nx-seg-btn' + (decision === 'new' ? ' is-active' : '')} onClick={() => onDecision('new')} disabled={!!duplicate}>
              <NexusIcon name="Plus" size={11} /> New
            </button>
            {duplicate && (
              <button className={'nx-seg-btn' + (decision === 'merge' ? ' is-active' : '')} onClick={() => onDecision('merge')}>
                <NexusIcon name="GitBranch" size={11} /> Merge
              </button>
            )}
            <button className={'nx-seg-btn' + (decision === 'skip' ? ' is-active' : '')} onClick={() => onDecision('skip')}>
              <NexusIcon name="X" size={11} /> Skip
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatChip = ({ label, value, icon, tone, onClick, active }) => (
  <button className={'nx-import-stat' + (tone ? ' tone-' + tone : '') + (active ? ' is-active' : '')} onClick={onClick}>
    <NexusIcon name={icon} size={11} />
    <span className="nx-import-stat-val">{value}</span>
    <span className="nx-import-stat-label">{label}</span>
  </button>
);

const ProgressStep = ({ progress }) => {
  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
  return (
    <div className="nx-import-pad" style={{ textAlign: 'center', padding: '40px 24px' }}>
      <div className="nx-import-spin"><NexusIcon name="UploadCloud" size={32} /></div>
      <h4 style={{ margin: '14px 0 6px' }}>Importing records…</h4>
      <p className="nx-muted nx-mini">{progress.done} of {progress.total} processed</p>
      <div className="nx-import-bar"><div className="nx-import-bar-fill" style={{ width: pct + '%' }} /></div>
      <span className="nx-mini nx-muted">{pct}%</span>
    </div>
  );
};

const SummaryStep = ({ summary, schema }) => (
  <div className="nx-import-pad">
    <div className="nx-import-summary">
      <div className="nx-import-summary-icon"><NexusIcon name="Check" size={22} /></div>
      <h4>Import complete</h4>
      <p className="nx-muted">{summary.fileName} · {schema.label}</p>
      <div className="nx-import-stats" style={{ marginTop: 16, justifyContent: 'center' }}>
        <StatChip label="Created" value={summary.created} icon="Plus" tone="green" />
        <StatChip label="Merged" value={summary.merged} icon="GitBranch" tone="blue" />
        <StatChip label="Skipped" value={summary.skipped} icon="X" tone="gray" />
        {summary.failed > 0 && <StatChip label="Failed" value={summary.failed} icon="AlertCircle" tone="red" />}
      </div>
      {summary.errors.length > 0 && (
        <div className="nx-import-errlist">
          <strong>Errors</strong>
          {summary.errors.map((e, i) => <div key={i} className="nx-import-errline"><NexusIcon name="AlertCircle" size={10} /> {e}</div>)}
        </div>
      )}
    </div>
  </div>
);

const HistoryStep = () => {
  const [list, setList] = useStateI(loadHistory());
  const clear = () => {
    try { localStorage.removeItem(HISTORY_KEY); } catch (_) {}
    setList([]);
  };
  return (
    <div className="nx-import-pad">
      {list.length === 0 ? (
        <div className="nx-import-empty">No imports yet. Run your first import to see it here.</div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <span className="nx-muted nx-mini">{list.length} import{list.length === 1 ? '' : 's'}</span>
            <span style={{ flex: 1 }} />
            <Btn variant="ghost" size="sm" icon="Trash" onClick={clear}>Clear history</Btn>
          </div>
          <div className="nx-import-rows">
            {list.map(h => (
              <div key={h.id} className="nx-import-histrow">
                <span className="nx-import-histrow-icon"><NexusIcon name={IMPORT_SCHEMA[h.type]?.icon || 'File'} size={12} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="nx-import-row-title">{h.fileName}</div>
                  <div className="nx-import-row-sub">
                    {new Date(h.at).toLocaleString()} · {IMPORT_SCHEMA[h.type]?.label || h.type}
                  </div>
                </div>
                <span className="nx-tag tone-green">+{h.created}</span>
                <span className="nx-tag tone-blue">⇄{h.merged}</span>
                {h.skipped > 0 && <span className="nx-tag">⊘{h.skipped}</span>}
                {h.failed > 0 && <span className="nx-tag tone-red">!{h.failed}</span>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const ImportFoot = ({ step, stats, decisions, analysis, companyFix, defaultCompany, mapping, schema, onBack, onNext, onClose }) => {
  if (step === 'progress') return null;
  let primaryLabel = 'Continue';
  let primaryIcon = 'ArrowRight';
  let disabled = false;
  let info = null;

  if (step === 'upload') return null; // upload has its own button
  if (step === 'map') {
    const mapped = new Set(Object.values(mapping));
    const missing = schema.fields.filter(f => f.required && !mapped.has(f.key));
    disabled = missing.length > 0;
    info = disabled ? 'Map all required fields to continue.' : null;
    primaryLabel = 'Continue to review';
  }
  if (step === 'review') {
    const willImport = Object.values(decisions).filter(v => v !== 'skip').length;
    const missingFix = analysis.some(a => a.needsCompany && decisions[a.idx] !== 'skip' && !(companyFix[a.idx] || defaultCompany));
    disabled = willImport === 0 || missingFix;
    info = missingFix ? 'Assign a company to every row missing one.' : willImport + ' record' + (willImport === 1 ? '' : 's') + ' will be imported.';
    primaryLabel = 'Import ' + willImport + ' record' + (willImport === 1 ? '' : 's');
    primaryIcon = 'UploadCloud';
  }
  if (step === 'summary') {
    primaryLabel = 'Done';
    primaryIcon = 'Check';
  }
  if (step === 'history') {
    return (
      <div className="nx-import-foot">
        <span style={{ flex: 1 }} />
        <Btn variant="ghost" onClick={onClose}>Close</Btn>
      </div>
    );
  }

  return (
    <div className="nx-import-foot">
      {step !== 'summary' && step !== 'upload' && <Btn variant="ghost" icon="ArrowLeft" onClick={onBack}>Back</Btn>}
      <span className="nx-muted nx-mini">{info}</span>
      <span style={{ flex: 1 }} />
      {step !== 'summary' && <Btn variant="ghost" onClick={onClose}>Cancel</Btn>}
      <Btn
        variant="primary"
        icon={primaryIcon}
        disabled={disabled}
        onClick={step === 'summary' ? onClose : onNext}
      >{primaryLabel}</Btn>
    </div>
  );
};
